import { useCallback, useLayoutEffect, useRef, type RefObject } from "react";
import { DropdownPanelCommandItem } from "@/components/molecules/dropdownPanel/dropdownPanelCommandItem";
import type { PickerListItem } from "./pickerList";

export interface PickerListRowProps {
  item: PickerListItem;
  value: string;
  isHighlighted: boolean;
  /** When the filtered set changes but length stays the same, scroll must still run. */
  itemsScrollKey: string;
  onSelect: () => void;
  firstItemRef?: RefObject<HTMLDivElement | null>;
  isFirst: boolean;
}

/**
 * Per-row scroll (react-select / listbox pattern): cmdk may not scroll when selection is
 * driven from outside the Command (e.g. editor-focused suggestion). cmdk also overwrites
 * `id` on CommandItem, so we use a ref on each row instead of getElementById.
 */
export const PickerListRow = ({
  item,
  value,
  isHighlighted,
  itemsScrollKey,
  onSelect,
  firstItemRef,
  isFirst,
}: PickerListRowProps) => {
  const localRef = useRef<HTMLDivElement | null>(null);

  const setRef = useCallback(
    (el: HTMLDivElement | null) => {
      localRef.current = el;
      if (isFirst && firstItemRef) {
        firstItemRef.current = el;
      }
    },
    [isFirst, firstItemRef],
  );

  useLayoutEffect(() => {
    if (!isHighlighted) return;
    localRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [isHighlighted, itemsScrollKey]);

  return (
    <DropdownPanelCommandItem
      ref={setRef}
      value={value}
      onMouseDown={(e) => e.preventDefault()}
      onSelect={() => {
        onSelect();
      }}
    >
      {item.label}
    </DropdownPanelCommandItem>
  );
};
